import {
  Input,
  useDisclosure,
  Button,
  FormControl,
  HStack,
  VStack,
  FormErrorMessage,
} from "@chakra-ui/react";
import { TransactionType } from "@syla/shared/types/models/TransactionBase";
import { get } from "lodash";
import React, { useEffect } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { amountInvalid } from "../../../../helper/forms/stringAsNumberInputField";
import { StandardSelectOption } from "../../../selector/parts/SelectOption";
import { SingleSelector } from "../../../selector/SingleSelector";
import { maxAssetsToDisplay } from "../ledgerInputConstants";

export const LedgerInputs = ({
  name,
  list,
  isRequired,
}: {
  name: string;
  list: StandardSelectOption[];
  isRequired: boolean;
}): JSX.Element => {
  const {
    setValue,
    control,
    formState: { errors },
    watch,
  } = useFormContext<any>();

  const {
    isOpen,
    onClose: closeCustomMarketValueInput,
    onToggle,
  } = useDisclosure();

  const transactionType: TransactionType = watch("type");

  useEffect(() => {
    closeCustomMarketValueInput();
    setValue(`${name}.customMarketValue`, undefined);
  }, [closeCustomMarketValueInput, name, setValue, transactionType]);

  return (
    <VStack w="full" alignItems="flex-start">
      <HStack w="full" alignItems="flex-start" spacing={2}>
        <FormControl w="full" isInvalid={!!get(errors, `${name}.amount.type`)}>
          <Controller
            control={control}
            name={`${name}.amount`}
            rules={{
              required: isRequired ? "Enter a quantity" : false,
              validate: (value) => {
                // if a value is present, it must be a valid value
                if (value && amountInvalid({ value })) {
                  return "Enter a valid quantity";
                }

                return true;
              },
            }}
            render={({ field: { onChange, value } }) => (
              <Input
                // NOTE: transactions no longer have the fee ledgers array, the hook form will keep it to maintain a single form for fees and just build the alt in ledgers and alt out ledgers array in the on submit handler
                w="full"
                isRequired={!name.includes("feeLedgers")}
                value={value}
                type="string"
                placeholder="Quantity"
                onChange={(event) => onChange(event.target.value)}
              />
            )}
          />
          <FormErrorMessage>
            {get(errors, `${name}.amount.message`)}
          </FormErrorMessage>
        </FormControl>

        <FormControl w="auto" isInvalid={!!get(errors, `${name}.assetId.type`)}>
          <Controller
            // NOTE: transactions no longer have the fee ledgers array, the hook form will keep it to maintain a single form for fees and just build the alt in ledgers and alt out ledgers array in the on submit handler
            control={control}
            name={`${name}.assetId`}
            rules={{ required: isRequired ? "Select an asset" : false }}
            render={({ field }) => (
              <SingleSelector
                moveSelectedToTop
                enableDeselect
                value={field.value || undefined}
                onChange={(value) => value && field.onChange(value ?? "")}
                options={list}
                maxOptionsDisplayed={maxAssetsToDisplay}
                placeholder="Asset"
                enableSearch
                buttonProps={{
                  minWidth: "10rem",
                }}
                isInvalid={!!get(errors, `${name}.assetId.type`)}
                isLoading={!list.length}
              />
            )}
          />
          <FormErrorMessage>
            {get(errors, `${name}.assetId.message`)}
          </FormErrorMessage>
        </FormControl>
      </HStack>

      <Button
        variant="ghost"
        bgColor="transparent"
        onClick={() => {
          onToggle();
          setValue(`${name}.customMarketValue`, undefined);
        }}
        fontSize="sm"
      >
        {isOpen ? "- Remove Market Value" : "+ Add Market Value"} (Optional)
      </Button>

      {isOpen && (
        <>
          <HStack alignItems="flex-start" spacing={2}>
            {/* ----------------------- ledger marketValue --------------------- */}
            <FormControl
              w="full"
              isInvalid={
                !!get(errors, `${name}.customMarketValue.marketValue.type`)
              }
              isRequired={isOpen}
            >
              <Controller
                control={control}
                name={`${name}.customMarketValue.marketValue`}
                rules={{ required: isOpen ? "Enter a market value" : false }}
                render={({ field: { onChange, value } }) => (
                  <Input
                    // NOTE: transactions no longer have the fee ledgers array, the hook form will keep it to maintain a single form for fees and just build the alt in ledgers and alt out ledgers array in the on submit handler
                    isRequired={isOpen}
                    value={value}
                    type="string"
                    placeholder="Market Value"
                    w="full"
                    onChange={(event) => onChange(event.target.value)}
                  />
                )}
              />
              <FormErrorMessage>
                {get(errors, `${name}.customMarketValue.marketValue.message`)}
              </FormErrorMessage>
            </FormControl>

            {/* ----------------------- ledger marketValueAsset --------------------- */}
            <FormControl
              w="auto"
              isInvalid={
                !!get(errors, `${name}.customMarketValue.assetId.type`)
              }
              isRequired={isOpen}
            >
              <Controller
                control={control}
                name={`${name}.customMarketValue.assetId`}
                rules={{ required: isOpen ? "Select an asset" : false }}
                render={({ field }) => (
                  <SingleSelector
                    moveSelectedToTop
                    enableDeselect
                    value={field.value || undefined}
                    onChange={(value) => {
                      field.onChange(value ?? "");
                    }}
                    options={list}
                    maxOptionsDisplayed={maxAssetsToDisplay}
                    placeholder="Asset"
                    enableSearch
                    buttonProps={{
                      minWidth: "10rem",
                    }}
                    isLoading={!list.length}
                    isInvalid={
                      !!get(errors, `${name}.customMarketValue.assetId.type`)
                    }
                  />
                )}
              />
              <FormErrorMessage>
                {get(errors, `${name}.customMarketValue.assetId.message`)}
              </FormErrorMessage>
            </FormControl>
          </HStack>
        </>
      )}
    </VStack>
  );
};
